// Direct module imports (not the '@/isf' barrel): this file is on the eager
// path of most of the app, and both modules are tiny and dependency-free.
import { isISFSource } from '@/isf/detect';
import { stripVendorHeader } from '@/isf/vendorHeader';

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const CLAUDE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export function isImageForClaude(mimeType: string): boolean {
  return CLAUDE_IMAGE_TYPES.includes(mimeType);
}

export function isPdfFile(mimeType: string): boolean {
  return mimeType === 'application/pdf';
}

export function isClaudeSupported(mimeType: string): boolean {
  return isImageForClaude(mimeType) || isPdfFile(mimeType);
}

const IMAGE_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'image/bmp', 'image/tiff', 'image/svg+xml',
];

const VIDEO_TYPES = [
  'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo',
];

export function isImageFile(mimeType: string): boolean {
  return IMAGE_TYPES.includes(mimeType);
}

export function isVideoFile(mimeType: string): boolean {
  return VIDEO_TYPES.includes(mimeType);
}

export function isMediaFile(mimeType: string): boolean {
  return isImageFile(mimeType) || isVideoFile(mimeType);
}

export function getMediaType(mimeType: string): 'image' | 'video' | 'other' {
  if (isImageFile(mimeType)) return 'image';
  if (isVideoFile(mimeType)) return 'video';
  return 'other';
}

/**
 * Canonical preview-kind classification used by every preview/thumbnail
 * consumer (LibraryPreview, thumbnailService, the resolver, the eager worker).
 *
 * MIME is authoritative for image/video/audio — browsers report those
 * reliably — but it is empty or inconsistent for `.obj`/`.psd`/`.txt`/`.csv`
 * (and the server stores `file.type` as-is). For those, the filename
 * extension is the source of truth, so it is checked first.
 */
export type PreviewKind =
  | 'image' | 'video' | 'audio' | 'pdf'
  | 'model' | 'psd' | 'text' | 'sheet' | 'drawing' | 'code' | 'presentation'
  | 'shader' | 'other';

/**
 * Source-code-like extensions → language id. Anything listed here
 * previews as `'code'` (the code editor with syntax highlighting) rather than
 * `'text'` (the rich-text editor, which would rewrite the file as HTML on the
 * first save — fatal for a `.json` or `.gcode`). Keys are lower-case
 * extensions; values are the ids the editor kit's highlighter knows
 * (LibraryFileKind.cpp: glsl, gcode ...).
 */
const CODE_LANGUAGE_BY_EXT: Record<string, string> = {
  // Toolpaths
  gcode: 'gcode', nc: 'gcode', ngc: 'gcode', tap: 'gcode', cnc: 'gcode',
  // Shaders
  glsl: 'glsl', frag: 'glsl', vert: 'glsl', fs: 'glsl', vs: 'glsl',
  // Data / config
  json: 'json', jsonc: 'json', json5: 'json', geojson: 'json',
  yaml: 'yaml', yml: 'yaml', toml: 'ini', ini: 'ini', cfg: 'ini', conf: 'ini', env: 'ini',
  xml: 'xml', plist: 'xml', svg: 'xml', xaml: 'xml',
  // Web
  js: 'javascript', mjs: 'javascript', cjs: 'javascript', jsx: 'javascript',
  ts: 'typescript', mts: 'typescript', cts: 'typescript', tsx: 'typescript',
  html: 'html', htm: 'html', vue: 'html', css: 'css', scss: 'scss', less: 'less',
  // General-purpose languages
  py: 'python', pyw: 'python', rb: 'ruby', php: 'php', lua: 'lua', pl: 'perl',
  c: 'c', h: 'c', cpp: 'cpp', cc: 'cpp', cxx: 'cpp', hpp: 'cpp', hh: 'cpp', ino: 'cpp',
  // EAGLE / Fusion Electronics User Language Programs: C-like syntax.
  ulp: 'cpp',
  cs: 'csharp', java: 'java', kt: 'kotlin', swift: 'swift', go: 'go', rs: 'rust',
  scala: 'scala', dart: 'dart', r: 'r', m: 'objective-c', mm: 'objective-c',
  // Shell / build / docs / misc
  sh: 'shell', bash: 'shell', zsh: 'shell', fish: 'shell', bat: 'bat', cmd: 'bat', ps1: 'powershell',
  makefile: 'makefile', mk: 'makefile', dockerfile: 'dockerfile', cmake: 'cmake',
  md: 'markdown', markdown: 'markdown', sql: 'sql', graphql: 'graphql', gql: 'graphql',
  proto: 'protobuf', log: 'plaintext', diff: 'plaintext', patch: 'plaintext',
};

/**
 * Monaco language id for a filename, or `null` when the extension isn't a
 * recognised code type. Files like `Makefile`/`Dockerfile` (no extension) are
 * matched on their whole lower-cased name.
 */
export function codeLanguageForFilename(filename: string): string | null {
  const ext = fileExtension(filename);
  if (ext && CODE_LANGUAGE_BY_EXT[ext]) return CODE_LANGUAGE_BY_EXT[ext];
  const base = (filename || '').split('/').pop()?.toLowerCase() ?? '';
  return CODE_LANGUAGE_BY_EXT[base] ?? null;
}

const EXT_PREVIEW_KIND: Record<string, PreviewKind> = {
  obj: 'model',
  stl: 'model',
  // B-rep CAD — tessellated in-browser by OpenCascade WASM (services/modelLoader).
  step: 'model', stp: 'model', iges: 'model', igs: 'model', brep: 'model',
  // Scene / mesh formats — Three.js loaders (materials + textures preserved).
  glb: 'model', gltf: 'model', ply: 'model', dae: 'model', fbx: 'model', '3ds': 'model',
  '3mf': 'model', wrl: 'model', vrml: 'model',
  // Recognised but not renderable in a browser — the 3D pane explains why
  // (no open parser exists) instead of a generic "preview unavailable".
  x_t: 'model', x_b: 'model', x3d: 'model',
  // Parametric CAD document (feature timeline JSON) — opens in the CAD
  // editor; the preview pane points at Edit instead of tessellating.
  sgcad: 'model',
  psd: 'psd',
  txt: 'text',
  csv: 'sheet',
  tsv: 'sheet',
  xlsx: 'sheet',
  pdf: 'pdf',
  // Illustrator files have a PDF stream embedded by default (CS2+) and render
  // fine in the browser's PDF viewer. The rare "no PDF compatibility" save
  // mode will fail to render and fall through to "Preview unavailable".
  ai: 'pdf',
  dxf: 'drawing',
  // ISF shaders — the extension every Shader Chat conversation is mirrored to
  // (and what Hypno 2 / VDMX / Resolume load). Opens the shader pane (live
  // render + "Open in Shader Chat"); a `.fs` whose text turns out not to be
  // ISF falls back to the code view there (see previewKindForShaderContent).
  // `.frag`/`.glsl`/`.vert` stay plain code: they're never mirror files.
  fs: 'shader',
  // Slide decks — OOXML (.pptx family), legacy binary .ppt, OpenDocument
  // .odp and Keynote .key. Rendered by services/slideRender (viewer,
  // thumbnails, present mode); edited by LibraryPanel/slides.
  pptx: 'presentation', pptm: 'presentation', ppsx: 'presentation', potx: 'presentation',
  ppt: 'presentation', odp: 'presentation', otp: 'presentation', key: 'presentation',
  // Audio formats — extension is checked first because browsers sometimes
  // report unreliable MIME types for audio (e.g. application/octet-stream
  // for .wav on Windows, or audio/x-wav variants).
  mp3: 'audio', wav: 'audio', flac: 'audio', m4a: 'audio',
  ogg: 'audio', oga: 'audio', aac: 'audio', opus: 'audio',
  aiff: 'audio', aif: 'audio',
};

/**
 * Content-based refinement of the `'shader'` kind, for callers that have the
 * file text: `'shader'` when it is ISF (the SHADYGRAM vendor key is stripped
 * first so it can never be what makes a file look like ISF), else `'code'`.
 */
export function previewKindForShaderContent(text: string): 'shader' | 'code' {
  return isISFSource(stripVendorHeader(text)) ? 'shader' : 'code';
}

export function fileExtension(filename: string): string {
  const dot = (filename || '').lastIndexOf('.');
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

// Derive a media upload's thumbnail URL from any URL that contains its id (the
// droplet `/api/media-uploads/:id`, a signed CDN `.../media/:id`, etc.). Used as a
// VIDEO poster so a <video> shows the still while it loads instead of black. The
// /thumb endpoint serves a public, CORS-enabled JPEG for published media. Returns
// undefined when no upload id is present (e.g. an external URL).
const MEDIA_ID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** The media upload id embedded in any media URL (droplet, CDN, thumb, …), or
 *  undefined for URLs that don't reference an upload (e.g. an external image). */
export function mediaIdFromSrc(src: string | null | undefined): string | undefined {
  const m = MEDIA_ID_RE.exec(src || '');
  return m ? m[1] : undefined;
}

export function thumbUrlFromMediaSrc(src: string | null | undefined): string | undefined {
  const id = mediaIdFromSrc(src);
  return id ? `/api/media-uploads/${id}/thumb` : undefined;
}

export function getPreviewKind(mimeType: string, filename: string): PreviewKind {
  // Extension first — it disambiguates cases the browser mislabels, e.g. a
  // `.psd` reported as `image/vnd.adobe.photoshop` must not be treated as a
  // browser-renderable image.
  const byExt = EXT_PREVIEW_KIND[fileExtension(filename)];
  if (byExt) return byExt;

  const mime = mimeType || '';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  // Source-like files open in the code editor. Checked AFTER the media MIMEs
  // because extensions collide: `.ts` is also MPEG-TS video, `.m` is
  // Objective-C or MATLAB, and a browser-reported video/* is trustworthy
  // where a text MIME is not. SVG stays an image even with an empty MIME —
  // browsers render it natively.
  if (fileExtension(filename) !== 'svg' && codeLanguageForFilename(filename)) return 'code';
  return 'other';
}

// ---------------------------------------------------------------------------
// Upload MIME resolution
//
// A File's `type` is set by the OS/browser picker and is not reliable: iOS
// Safari has been observed to report `text/html` (and "") for camera-roll
// picks. Forwarding that as the upload Content-Type makes the Matrix
// homeserver reject the upload ("text/html is not a valid mime type"). The
// file's leading bytes are authoritative, so they are sniffed first.
// ---------------------------------------------------------------------------

export const EXT_MIME: Record<string, string> = {
  mp4: 'video/mp4', m4v: 'video/x-m4v', webm: 'video/webm', mov: 'video/quicktime',
  ogv: 'video/ogg', avi: 'video/x-msvideo',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', bmp: 'image/bmp', tif: 'image/tiff', tiff: 'image/tiff',
  heic: 'image/heic', heif: 'image/heif', svg: 'image/svg+xml',
  mp3: 'audio/mpeg', m4a: 'audio/mp4', wav: 'audio/wav', ogg: 'audio/ogg',
  oga: 'audio/ogg', flac: 'audio/flac', aac: 'audio/aac',
  aiff: 'audio/x-aiff', aif: 'audio/x-aiff', opus: 'audio/ogg',
  pdf: 'application/pdf', zip: 'application/zip', json: 'application/json',
  step: 'model/step', stp: 'model/step', iges: 'model/iges', igs: 'model/iges',
  obj: 'model/obj', stl: 'model/stl', glb: 'model/gltf-binary', gltf: 'model/gltf+json',
  ply: 'application/x-ply', dae: 'model/vnd.collada+xml', fbx: 'application/octet-stream',
  '3ds': 'application/x-3ds', '3mf': 'model/3mf', wrl: 'model/vrml', vrml: 'model/vrml',
  x3d: 'model/x3d+xml', x_t: 'application/octet-stream', x_b: 'application/octet-stream',
  sgcad: 'application/x-sgcad',
  txt: 'text/plain', csv: 'text/csv', md: 'text/markdown',
  gcode: 'text/x-gcode', nc: 'text/x-gcode', ngc: 'text/x-gcode',
  ulp: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  pptm: 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
  ppsx: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  potx: 'application/vnd.openxmlformats-officedocument.presentationml.template',
  ppt: 'application/vnd.ms-powerpoint',
  odp: 'application/vnd.oasis.opendocument.presentation',
  otp: 'application/vnd.oasis.opendocument.presentation-template',
  key: 'application/x-iwork-keynote-sffkey',
};

/**
 * Detect a file's MIME type from its magic bytes. Returns `null` when the
 * leading bytes match no known signature (e.g. plain text, .doc, .csv).
 */
export async function sniffMimeType(file: File): Promise<string | null> {
  let header: Uint8Array;
  try {
    header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  } catch {
    return null;
  }
  if (header.length < 4) return null;

  const at = (sig: number[], offset = 0): boolean =>
    sig.every((b, i) => header[offset + i] === b);
  const tag = (offset: number, len: number): string => {
    let s = '';
    for (let i = 0; i < len; i++) s += String.fromCharCode(header[offset + i] ?? 0);
    return s;
  };

  // Images
  if (at([0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (at([0x89, 0x50, 0x4e, 0x47])) return 'image/png';
  if (at([0x47, 0x49, 0x46, 0x38])) return 'image/gif';
  if (tag(0, 4) === 'RIFF' && tag(8, 4) === 'WEBP') return 'image/webp';
  if (at([0x42, 0x4d])) return 'image/bmp';
  if (at([0x49, 0x49, 0x2a, 0x00]) || at([0x4d, 0x4d, 0x00, 0x2a])) return 'image/tiff';

  // ISO base media container — MP4 / MOV / M4A / HEIC share an `ftyp` box
  // whose 4-char brand disambiguates the concrete format.
  if (tag(4, 4) === 'ftyp') {
    const brand = tag(8, 4);
    if (brand === 'qt  ') return 'video/quicktime';
    if (brand.startsWith('M4A')) return 'audio/mp4';
    if (/^(heic|heix|hev[cmsx]|hei[ms]|mif1|msf1)/.test(brand)) return 'image/heic';
    return 'video/mp4';
  }

  // Other audio / video
  if (at([0x1a, 0x45, 0xdf, 0xa3])) return 'video/webm';
  if (tag(0, 4) === 'OggS') return 'audio/ogg';
  if (tag(0, 4) === 'RIFF' && tag(8, 4) === 'WAVE') return 'audio/wav';
  if (at([0x49, 0x44, 0x33]) || (header[0] === 0xff && (header[1] & 0xe0) === 0xe0)) {
    return 'audio/mpeg';
  }

  // Documents / archives
  if (tag(0, 4) === '%PDF') return 'application/pdf';
  if (at([0x50, 0x4b, 0x03, 0x04])) return 'application/zip';

  return null;
}

/** `file.type` values too unreliable to forward to an upload as-is. */
function isUntrustedUploadType(type: string): boolean {
  return !type || type === 'text/html' || type === 'application/octet-stream';
}

/**
 * Resolve the MIME type to declare when uploading `file`. Trusts, in order:
 * the sniffed magic bytes, the filename extension, then `file.type` — and
 * never forwards the spurious `text/html` iOS reports for camera-roll picks.
 */
export async function resolveUploadMimeType(file: File): Promise<string> {
  const sniffed = await sniffMimeType(file);
  const byExt = EXT_MIME[fileExtension(file.name)];

  // Sniffing reads real bytes so it wins for media — but the ZIP signature is
  // shared by docx/xlsx/pptx/epub, so a specific extension overrides it.
  if (sniffed && !(sniffed === 'application/zip' && byExt)) return sniffed;
  if (byExt) return byExt;
  return isUntrustedUploadType(file.type) ? 'application/octet-stream' : file.type;
}

/**
 * Backwards-compatible data-URL thumbnail wrapper. New code should call
 * `generateThumbnailBlob` from `services/thumbnailService` directly — the
 * blob form skips a base64 round-trip on the wire. This shim is retained
 * because conversation messages embed thumbnails as data URLs in the
 * encrypted message payload (`MessageAttachmentInfo.thumbnail` /
 * `.videoThumbnail`), and that storage format hasn't moved to B2.
 */
export async function generateThumbnail(file: File): Promise<string | undefined> {
  try {
    const { generateThumbnailBlob } = await import('../services/thumbnailService');
    const blob = await generateThumbnailBlob(file);
    if (!blob) return undefined;
    return await new Promise<string | undefined>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}
